import React, { useEffect, useState } from 'react';
import { db, storage } from '../../firebase';
import { collection, onSnapshot, query, orderBy, addDoc, doc, updateDoc, deleteDoc, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { Pencil, Trash2, PlusCircle, UploadCloud } from 'lucide-react';

const equipeOptions = [
  'SENIORS N2', 'SENIORS R1', 'U18 R1', 'U17 R2', 'U16 R1', 'U15 R1', 'U14 Ligue',
  'U18F R2', 'U16 D2', 'U14 D1', 'U13 Niveau A', 'U12 Niveau A', 'U11 Niveau A', 'U10 Niveau A', 'Autre'
];
const posteOptions = ['Gardien', 'Défenseur', 'Milieu', 'Attaquant', 'Polyvalent', 'Autre'];

const defaultForm = {
  prenom: '',
  nom: '',
  equipe: '',
  poste: '',
  numero: '',
  dateNaissance: '',
  nationalite: '',
  saison: '',
  photo: '',
  matchsJoues: 0,
  buts: 0,
  passes: 0,
  cartonsJaunes: 0,
  cartonsRouges: 0,
  minutesJouees: 0,
  titularisations: 0,
  remplacements: 0,
  cleanSheets: 0,
  description: '',
  visible: true,
  face_photo: '',
};

const Players = () => {
  const [joueurs, setJoueurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [equipeFilter, setEquipeFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [photoFile, setPhotoFile] = useState(null);
  const [facePhotoFile, setFacePhotoFile] = useState(null);
  const [adding, setAdding] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [editId, setEditId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [equipes, setEquipes] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkEquipe, setBulkEquipe] = useState('');

  useEffect(() => {
    const q = query(collection(db, 'equipes'), orderBy('ordre', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      setEquipes(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'joueurs'), orderBy('nom', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      setJoueurs(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const joueursFiltres = equipeFilter
    ? joueurs.filter(j => j.equipe === equipeFilter)
    : joueurs;

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handlePhotoChange = e => {
    if (e.target.files[0]) {
      setPhotoFile(e.target.files[0]);
    }
  };

  const handleFacePhotoChange = e => {
    if (e.target.files[0]) {
      setFacePhotoFile(e.target.files[0]);
    }
  };

  const uploadPhoto = async (file, nom, joueurId = null) => {
    if (!file) return '';
    const ext = file.name.split('.').pop();
    const safeName = nom.replace(/[^a-zA-Z0-9]/g, '_');
    const path = `photos_joueurs/${safeName}_${Date.now()}${joueurId ? '_' + joueurId : ''}.${ext}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  };

  const uploadFacePhoto = async (file, nom, joueurId = null) => {
    if (!file) return '';
    const ext = file.name.split('.').pop();
    const safeName = nom.replace(/[^a-zA-Z0-9]/g, '_');
    const path = `photos_joueurs/face_${safeName}_${Date.now()}${joueurId ? '_' + joueurId : ''}.${ext}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  };

  const handleAddOrEditJoueur = async (e) => {
    e.preventDefault();
    setAdding(true);
    setFeedback('');
    try {
      let photoUrl = form.photo;
      let facePhotoUrl = form.face_photo;
      if (photoFile) {
        photoUrl = await uploadPhoto(photoFile, form.nom, editId);
      }
      if (facePhotoFile) {
        facePhotoUrl = await uploadFacePhoto(facePhotoFile, form.nom, editId);
      }
      const joueurData = {
        ...form,
        photo: photoUrl,
        face_photo: facePhotoUrl,
        matchsJoues: Number(form.matchsJoues) || 0,
        buts: Number(form.buts) || 0,
        passes: Number(form.passes) || 0,
        cartonsJaunes: Number(form.cartonsJaunes) || 0,
        cartonsRouges: Number(form.cartonsRouges) || 0,
        minutesJouees: Number(form.minutesJouees) || 0,
        titularisations: Number(form.titularisations) || 0,
        remplacements: Number(form.remplacements) || 0,
        cleanSheets: Number(form.cleanSheets) || 0,
        visible: !!form.visible,
        updatedAt: new Date(),
      };
      if (editId) {
        await updateDoc(doc(db, 'joueurs', editId), joueurData);
        setFeedback('Joueur modifié avec succès !');
      } else {
        await addDoc(collection(db, 'joueurs'), {
          ...joueurData,
          createdAt: new Date(),
        });
        setFeedback('Joueur ajouté avec succès !');
      }
      setShowModal(false);
      setForm(defaultForm);
      setPhotoFile(null);
      setFacePhotoFile(null);
      setEditId(null);
    } catch (err) {
      setFeedback("Erreur : " + err.message);
    }
    setAdding(false);
    setTimeout(() => setFeedback(''), 3000);
  };

  const handleEdit = (joueur) => {
    setForm({
      prenom: joueur.prenom || '',
      nom: joueur.nom || '',
      equipe: joueur.equipe || '',
      poste: joueur.poste || '',
      numero: joueur.numero || '',
      dateNaissance: joueur.dateNaissance || '',
      nationalite: joueur.nationalite || '',
      saison: joueur.saison || '',
      photo: joueur.photo || '',
      matchsJoues: joueur.matchsJoues ?? 0,
      buts: joueur.buts ?? 0,
      passes: joueur.passes ?? 0,
      cartonsJaunes: joueur.cartonsJaunes ?? 0,
      cartonsRouges: joueur.cartonsRouges ?? 0,
      minutesJouees: joueur.minutesJouees ?? 0,
      titularisations: joueur.titularisations ?? 0,
      remplacements: joueur.remplacements ?? 0,
      cleanSheets: joueur.cleanSheets ?? 0,
      description: joueur.description || '',
      visible: joueur.visible !== false,
      face_photo: joueur.face_photo || '',
    });
    setEditId(joueur.id);
    setShowModal(true);
    setPhotoFile(null);
    setFacePhotoFile(null);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      // Supprimer la photo du storage si existant
      if (toDelete.photo && toDelete.photo.includes('photos_joueurs')) {
        try {
          const photoRef = ref(storage, toDelete.photo);
          await deleteObject(photoRef);
        } catch (e) { /* ignore */ }
      }
      await deleteDoc(doc(db, 'joueurs', toDelete.id));
      setFeedback('Joueur supprimé.');
    } catch (err) {
      setFeedback("Erreur lors de la suppression : " + err.message);
    }
    setShowConfirm(false);
    setToDelete(null);
    setTimeout(() => setFeedback(''), 3000);
  };

  // Sélection individuelle
  const handleSelect = (id) => {
    setSelectedIds(ids => ids.includes(id) ? ids.filter(i => i !== id) : [...ids, id]);
  };
  // Sélection tous
  const handleSelectAll = () => {
    if (selectedIds.length === joueursFiltres.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(joueursFiltres.map(j => j.id));
    }
  };

  // Modification groupée d'équipe
  const handleBulkEditEquipe = async (e) => {
    e.preventDefault();
    if (!bulkEquipe || selectedIds.length === 0) return;
    setAdding(true);
    try {
      for (const id of selectedIds) {
        await updateDoc(doc(db, 'joueurs', id), { equipe: bulkEquipe });
      }
      setFeedback('Équipe modifiée pour les joueurs sélectionnés !');
      setShowBulkEdit(false);
      setSelectedIds([]);
      setBulkEquipe('');
    } catch (err) {
      setFeedback('Erreur lors de la modification groupée : ' + err.message);
    }
    setAdding(false);
    setTimeout(() => setFeedback(''), 3000);
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Gestion des Joueurs</h1>
      <div className="flex items-center gap-4 mb-6">
        <button className="flex items-center gap-2 bg-[#FFE400] hover:bg-[#BDFF00] text-[#001B56] font-bold px-4 py-2 rounded-xl shadow transition" onClick={() => { setShowModal(true); setEditId(null); setForm(defaultForm); setPhotoFile(null); setFacePhotoFile(null); }}>
          <PlusCircle size={20} /> Ajouter un joueur
        </button>
        {selectedIds.length > 0 && (
          <button className="bg-[#FFE400] text-[#001B56] font-bold px-4 py-2 rounded-xl shadow transition" onClick={() => setShowBulkEdit(true)}>
            Modifier {selectedIds.length} joueur(s)
          </button>
        )}
        <select
          className="px-4 py-2 rounded-xl bg-[#00238D] text-white border border-[#FFE400] font-semibold"
          value={equipeFilter}
          onChange={e => setEquipeFilter(e.target.value)}
        >
          <option value="">Toutes les équipes</option>
          {equipes.map(eq => (
            <option key={eq.id} value={eq.id}>{eq.nom}</option>
          ))}
        </select>
      </div>
      {feedback && <div className="mb-4 text-white font-semibold">{feedback}</div>}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <form onSubmit={handleAddOrEditJoueur} className="bg-[#00238D] text-white rounded-2xl shadow-2xl p-8 w-full max-w-2xl flex flex-col gap-4 animate-fade-in">
            <h2 className="text-xl font-bold mb-2">{editId ? 'Modifier le joueur' : 'Ajouter un joueur'}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-1">Prénom</label>
                <input type="text" name="prenom" value={form.prenom} onChange={handleChange} required className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
              </div>
              <div>
                <label className="block mb-1">Nom</label>
                <input type="text" name="nom" value={form.nom} onChange={handleChange} required className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
              </div>
              <div>
                <label className="block mb-1">Équipe</label>
                <select name="equipe" value={form.equipe} onChange={handleChange} required className="w-full px-3 py-2 rounded bg-[#001B56] text-white">
                  <option value="">Choisir...</option>
                  {equipes.map(eq => (
                    <option key={eq.id} value={eq.id}>{eq.nom}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block mb-1">Poste</label>
                <select name="poste" value={form.poste} onChange={handleChange} required className="w-full px-3 py-2 rounded bg-[#001B56] text-white">
                  <option value="">Choisir...</option>
                  {posteOptions.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block mb-1">Numéro</label>
                <input type="number" name="numero" value={form.numero} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
              </div>
              <div>
                <label className="block mb-1">Date de naissance</label>
                <input type="date" name="dateNaissance" value={form.dateNaissance} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
              </div>
              <div>
                <label className="block mb-1">Nationalité</label>
                <input type="text" name="nationalite" value={form.nationalite} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
              </div>
              <div>
                <label className="block mb-1">Saison</label>
                <input type="text" name="saison" value={form.saison} onChange={handleChange} placeholder="ex: 2023-2024" className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
              </div>
              <div className="md:col-span-2">
                <label className="block mb-1">Photo</label>
                <div className="flex items-center gap-2">
                  <input type="file" accept="image/*" onChange={handlePhotoChange} className="w-full text-white" />
                  {form.photo && !photoFile && (
                    <img src={form.photo} alt="photo" className="w-10 h-10 rounded-full bg-white" />
                  )}
                  {photoFile && (
                    <UploadCloud size={22} className="text-[#FFE400] animate-bounce" />
                  )}
                </div>
              </div>
              <div>
                <label className="block mb-1">Photo de Face</label>
                <div className="flex items-center gap-2">
                  <input type="file" accept="image/*" onChange={handleFacePhotoChange} className="w-full text-white" />
                  {form.face_photo && !facePhotoFile && (
                    <img src={form.face_photo} alt="face photo" className="w-10 h-10 rounded-full bg-white" />
                  )}
                  {facePhotoFile && (
                    <UploadCloud size={22} className="text-[#FFE400] animate-bounce" />
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div>
                <label className="block mb-1">Matchs joués</label>
                <input type="number" name="matchsJoues" value={form.matchsJoues} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
              </div>
              <div>
                <label className="block mb-1">Buts</label>
                <input type="number" name="buts" value={form.buts} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
              </div>
              <div>
                <label className="block mb-1">Passes</label>
                <input type="number" name="passes" value={form.passes} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
              </div>
              <div>
                <label className="block mb-1">Cartons jaunes</label>
                <input type="number" name="cartonsJaunes" value={form.cartonsJaunes} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
              </div>
              <div>
                <label className="block mb-1">Cartons rouges</label>
                <input type="number" name="cartonsRouges" value={form.cartonsRouges} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
              </div>
              <div>
                <label className="block mb-1">Minutes jouées</label>
                <input type="number" name="minutesJouees" value={form.minutesJouees} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
              </div>
              <div>
                <label className="block mb-1">Titularisations</label>
                <input type="number" name="titularisations" value={form.titularisations} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
              </div>
              <div>
                <label className="block mb-1">Remplacements</label>
                <input type="number" name="remplacements" value={form.remplacements} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
              </div>
              <div>
                <label className="block mb-1">Clean sheets</label>
                <input type="number" name="cleanSheets" value={form.cleanSheets} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
              </div>
            </div>
            <div className="mt-4">
              <label className="block mb-1">Description / Bio</label>
              <textarea name="description" value={form.description} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white min-h-[60px]" />
            </div>
            <div className="flex items-center gap-3 mt-2">
              <input type="checkbox" name="visible" checked={form.visible} onChange={handleChange} id="visible" />
              <label htmlFor="visible">Afficher ce joueur sur le site public</label>
            </div>
            <div className="flex justify-end gap-4 mt-4">
              <button type="button" className="px-4 py-2 rounded bg-gray-400 hover:bg-gray-500 text-white font-bold" onClick={() => { setShowModal(false); setEditId(null); setPhotoFile(null); setFacePhotoFile(null); }} disabled={adding}>Annuler</button>
              <button type="submit" className="px-6 py-2 rounded bg-[#FFE400] hover:bg-[#BDFF00] text-[#001B56] font-bold" disabled={adding}>{adding ? (editId ? 'Modification...' : 'Ajout...') : (editId ? 'Modifier' : 'Ajouter')}</button>
            </div>
          </form>
        </div>
      )}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-[#00238D] text-white rounded-2xl shadow-2xl p-8 w-full max-w-md flex flex-col gap-6 animate-fade-in">
            <h2 className="text-xl font-bold">Confirmer la suppression</h2>
            <p>Voulez-vous vraiment supprimer ce joueur ? Cette action est irréversible.</p>
            <div className="flex justify-end gap-4">
              <button className="px-4 py-2 rounded bg-gray-400 hover:bg-gray-500 text-white font-bold" onClick={() => { setShowConfirm(false); setToDelete(null); }}>Annuler</button>
              <button className="px-6 py-2 rounded bg-red-600 hover:bg-red-700 text-white font-bold" onClick={handleDelete}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
      {showBulkEdit && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <form onSubmit={handleBulkEditEquipe} className="bg-[#00238D] text-white rounded-2xl shadow-2xl p-8 w-full max-w-md flex flex-col gap-4 animate-fade-in">
            <h2 className="text-xl font-bold mb-2">Modifier l'équipe de {selectedIds.length} joueur(s)</h2>
            <select value={bulkEquipe} onChange={e => setBulkEquipe(e.target.value)} required className="w-full px-3 py-2 rounded bg-[#001B56] text-white">
              <option value="">Choisir une équipe...</option>
              {equipes.map(eq => (
                <option key={eq.id} value={eq.id}>{eq.nom}</option>
              ))}
            </select>
            <div className="flex justify-end gap-4 mt-4">
              <button type="button" className="px-4 py-2 rounded bg-gray-400 hover:bg-gray-500 text-white font-bold" onClick={() => setShowBulkEdit(false)} disabled={adding}>Annuler</button>
              <button type="submit" className="px-6 py-2 rounded bg-[#FFE400] hover:bg-[#BDFF00] text-[#001B56] font-bold" disabled={adding}>Valider</button>
            </div>
          </form>
        </div>
      )}
      {loading ? (
        <div className="text-white">Chargement...</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-[#00238D] rounded-xl shadow-lg">
            <thead>
              <tr className="text-[#FFE400] text-left">
                <th className="py-2 px-4"><input type="checkbox" checked={selectedIds.length === joueursFiltres.length && joueursFiltres.length > 0} onChange={handleSelectAll} /></th>
                <th className="py-2 px-4">Photo</th>
                <th className="py-2 px-4">Nom</th>
                <th className="py-2 px-4">Prénom</th>
                <th className="py-2 px-4">Équipe</th>
                <th className="py-2 px-4">Poste</th>
                <th className="py-2 px-4">Matchs</th>
                <th className="py-2 px-4">Buts</th>
                <th className="py-2 px-4">Passes</th>
                <th className="py-2 px-4">Cartons J.</th>
                <th className="py-2 px-4">Cartons R.</th>
                <th className="py-2 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {joueursFiltres.length === 0 ? (
                <tr><td colSpan={12} className="text-center text-white py-8">Aucun joueur enregistré.</td></tr>
              ) : (
                joueursFiltres.map(joueur => (
                  <tr key={joueur.id} className="border-b border-[#001B56] hover:bg-[#001B56]/40 transition">
                    <td className="py-2 px-4"><input type="checkbox" checked={selectedIds.includes(joueur.id)} onChange={() => handleSelect(joueur.id)} /></td>
                    <td className="py-2 px-4">
                      {joueur.photo ? (
                        <img src={joueur.photo} alt={joueur.nom} className="w-10 h-10 rounded-full object-cover bg-white" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[#FFE400] flex items-center justify-center text-[#001B56] font-bold">
                          {joueur.prenom?.[0] || joueur.nom?.[0] || '?'}
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-4 font-bold">{joueur.nom}</td>
                    <td className="py-2 px-4">{joueur.prenom}</td>
                    <td className="py-2 px-4">{equipes.find(eq => eq.id === joueur.equipe)?.nom || '—'}</td>
                    <td className="py-2 px-4">{joueur.poste}</td>
                    <td className="py-2 px-4">{joueur.matchsJoues ?? 0}</td>
                    <td className="py-2 px-4">{joueur.buts ?? 0}</td>
                    <td className="py-2 px-4">{joueur.passes ?? 0}</td>
                    <td className="py-2 px-4">{joueur.cartonsJaunes ?? 0}</td>
                    <td className="py-2 px-4">{joueur.cartonsRouges ?? 0}</td>
                    <td className="py-2 px-4 flex gap-2">
                      <button className="p-2 rounded hover:bg-[#FFE400]/20 transition" title="Éditer" onClick={() => handleEdit(joueur)}><Pencil size={18} /></button>
                      <button className="p-2 rounded hover:bg-red-500/30 transition" title="Supprimer" onClick={() => { setShowConfirm(true); setToDelete(joueur); }}><Trash2 size={18} /></button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Players; 